/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UsageTrackingModel } from './usage.tracking.model';

declare global {
  interface Window {
    digitalData: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class UsageTrackingService {
  constructor(private readonly router: Router, private readonly activatedRoute: ActivatedRoute) {
    // create 'digitalData' global window object
    window.digitalData = {
      pageInfo: {
        applicationName: '',
        asmsWebsiteId: null,
        pageName: '',
        uiFramework: '',
      },
      visitorProfile: {
        gmin: '',
      },
    };
  }

  /**
   * track user details from the template provided
   * */
  public assignApplicationDetails(model: UsageTrackingModel): void {
    window.digitalData.pageInfo.applicationName = model.applicationName;
    window.digitalData.pageInfo.asmsId = model.asmsId;
    window.digitalData.pageInfo.asmsWebsiteId = model.asmsWebsiteId;
    window.digitalData.pageInfo.uiFramework = model.uiFramework;
  }

  /**
   * track user details from the template provided
   * */
  public assignUserDetails(gmin: string | undefined): void {
    if (gmin) {
      window.digitalData.visitorProfile.gmin = gmin;
    }
  }

  /**
   * add router event to capture all of the routes throughout the app
   * */
  public initiatePageLoad(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      if (event instanceof RouterEvent && event.url) {
        const activatedRouteData = this.activatedRoute.snapshot.firstChild
          ? this.activatedRoute.snapshot.firstChild.data
          : '';
        let currentPageName = '';

        if (activatedRouteData) {
          // get page name from the activated route data
          currentPageName = activatedRouteData.title;
        } else {
          // treat slash as a separator and remove everything before it
          // convert camel case to title sentence case
          const result = event.url
            .substring(1)
            .replace(/([A-Z])/g, ' $1')
            .split('/')
            .pop();
          currentPageName = result ? result.charAt(0).toUpperCase() + result.slice(1) : '';
        }
        this.trackPage(currentPageName);
      }
      this.trackEvent('page-load');
    });
  }

  /**
   * start of the analytics service with router provided page names
   * */
  public start(model: UsageTrackingModel | undefined): void {
    if (model) {
      this.assignApplicationDetails(model);
      this.switchAdobeInstance(model.environment);
      this.initiatePageLoad();
      this.assignUserDetails(model.gmin);
    }
  }

  /**
   * start of the analytics service with user provided page names
   * */
  public startCustom(model: UsageTrackingModel | undefined): void {
    if (model) {
      this.assignApplicationDetails(model);
      this.switchAdobeInstance(model.environment);
      this.assignUserDetails(model.gmin);
    }
  }

  /**
   * run a custom event
   *
   * @param eventName - string
   * @param eventValue - string
   * */
  public trackEvent(eventName: string, eventValue: string = ''): void {
    const customEvent = new CustomEvent(eventName);
    document.body.dispatchEvent(customEvent);
  }

  /**
   * Assign page name to the window object
   *
   * @param pageName - string
   * */
  public trackPage(pageName: string): void {
    window.digitalData.pageInfo.pageName = pageName;
  }

  /**
   * switch statements to add adobe 'src' tag in html based on the current environment
   * */
  private switchAdobeInstance(environment: string): void {
    // create a dynamic script element
    const analyticsScript = document.createElement('script');

    // case statement to choose Analytics environment based on current environment name
    switch (environment) {
      case 'prod':
        analyticsScript.src = '//assets.adobedtm.com/ea8c57fea068/f6c9f8d09f20/launch-f0e839b934ee.min.js';
        break;
      case 'test':
        analyticsScript.src = '//assets.adobedtm.com/ea8c57fea068/f6c9f8d09f20/launch-106ad6c2388d-staging.min.js';
        break;
      default:
        analyticsScript.src = '//assets.adobedtm.com/ea8c57fea068/f6c9f8d09f20/launch-106ad6c2388d-staging.min.js';
    }
    analyticsScript.defer = true;
    document.head.appendChild(analyticsScript);
  }
}
